import type { MixState } from '../state/types';

/**
 * Graphe Web Audio du mixeur (cf. handoff § Mixeur & pad) :
 *
 *   <audio> → MediaElementSource
 *               → BiquadFilter (lowpass, Q = 6)
 *                   → outGain ─────────────────┐
 *                   → Delay (0.26 s)           │
 *                        ↘ feedbackGain (0.34) ↺ Delay
 *                        → wetGain → outGain ──┤
 *                                               → Analyser → destination
 *   LFO (0.75 Hz) → lfoGain → BiquadFilter.frequency
 *
 * Règle de conduite : cette classe ne lève jamais. Le site doit rester utilisable
 * sans son (fichier absent, Web Audio indisponible, contexte non sécurisé,
 * autoplay refusé). Chaque échec se traduit par un retour `false` ou par le
 * rappel `onMissing`, jamais par une exception qui remonterait dans le rendu.
 */

interface Handlers {
  /** Le fichier ne peut pas être lu : « steve.mp3 s'est échappé du bac. » */
  readonly onMissing: () => void;
}

/** Erreurs de `play()` qui ne veulent pas dire « fichier manquant ». */
const BENIGN_PLAY_ERRORS = new Set(['NotAllowedError', 'AbortError']);

export class AudioEngine {
  private readonly element: HTMLAudioElement;
  private readonly handlers: Handlers;

  private context: AudioContext | null = null;
  private filter: BiquadFilterNode | null = null;
  private wet: GainNode | null = null;
  private outGain: GainNode | null = null;
  private lfoGain: GainNode | null = null;
  private analyser: AnalyserNode | null = null;
  private bins: Uint8Array<ArrayBuffer> | null = null;

  private graphAttempted = false;
  private destroyed = false;
  /**
   * Dernier mixage demandé.
   *
   * Le graphe ne naît qu'au premier geste, longtemps après le premier réglage —
   * et il naît **bouché** : un `BiquadFilter` démarre à 350 Hz, pas à 18 kHz.
   * Sans cette mémoire, la chanson partait étouffée jusqu'au premier curseur
   * touché, qui semblait « réparer » le son.
   */
  private mix: MixState | null = null;

  constructor(src: string, handlers: Handlers) {
    this.handlers = handlers;
    this.element = new Audio();
    this.element.loop = true;
    // `none` : le fichier (1,5 Mo) n'est téléchargé qu'au premier geste de
    // l'utilisateur. Sur un pic de trafic, la majorité des visites ne le
    // demandent jamais.
    this.element.preload = 'none';
    this.element.crossOrigin = 'anonymous';
    this.element.src = src;
    this.element.addEventListener('error', this.reportMissing);
  }

  private readonly reportMissing = (): void => {
    if (!this.destroyed) this.handlers.onMissing();
  };

  /** Durée du morceau, ou 0 tant que les métadonnées ne sont pas connues. */
  get duration(): number {
    const value = this.element.duration;
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  /**
   * Construit le graphe au premier geste. Si `createMediaElementSource` est
   * refusé (fichier servi depuis une autre origine sans CORS), on abandonne le
   * graphe : la chanson passe alors en direct par l'élément `<audio>`, sans
   * effets, plutôt que de se retrouver muette.
   */
  private ensureGraph(): void {
    if (this.graphAttempted || this.destroyed) return;
    this.graphAttempted = true;

    const Ctor = window.AudioContext ?? window.webkitAudioContext;
    if (!Ctor) return;

    let context: AudioContext | null = null;
    try {
      context = new Ctor();
      const source = context.createMediaElementSource(this.element);

      const filter = context.createBiquadFilter();
      filter.type = 'lowpass';
      filter.Q.value = 6;

      const delay = context.createDelay(1);
      delay.delayTime.value = 0.26;

      const feedback = context.createGain();
      feedback.gain.value = 0.34;

      const wet = context.createGain();
      wet.gain.value = 0;

      const outGain = context.createGain();

      const analyser = context.createAnalyser();
      analyser.fftSize = 64;
      analyser.smoothingTimeConstant = 0.75;

      const lfo = context.createOscillator();
      lfo.frequency.value = 0.75;
      const lfoGain = context.createGain();
      lfoGain.gain.value = 0;

      lfo.connect(lfoGain);
      lfoGain.connect(filter.frequency);
      lfo.start();

      source.connect(filter);
      filter.connect(outGain);
      filter.connect(delay);
      delay.connect(feedback);
      feedback.connect(delay);
      delay.connect(wet);
      wet.connect(outGain);
      outGain.connect(analyser);
      analyser.connect(context.destination);

      this.context = context;
      this.filter = filter;
      this.wet = wet;
      this.outGain = outGain;
      this.lfoGain = lfoGain;
      this.analyser = analyser;
      this.bins = new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount));

      // Le graphe vient de naître à ses valeurs d'usine : on lui repasse
      // aussitôt les curseurs tels qu'ils sont à l'écran.
      if (this.mix) this.applyMix(this.mix);
    } catch {
      // Graphe inutilisable : on ferme le contexte pour ne pas laisser un
      // AudioContext orphelin (chaque onglet en a un nombre limité).
      try {
        void context?.close();
      } catch {
        /* rien à faire de plus */
      }
      this.context = null;
      this.analyser = null;
    }
  }

  /** Reprend le contexte suspendu par la politique d'autoplay du navigateur. */
  private resume(): void {
    if (this.context?.state === 'suspended') {
      void this.context.resume().catch(() => undefined);
    }
  }

  /** Lance la lecture. Renvoie `false` si le son n'a pas démarré. */
  async play(): Promise<boolean> {
    if (this.destroyed) return false;
    this.ensureGraph();
    this.resume();
    try {
      await this.element.play();
      return true;
    } catch (error) {
      const name = error instanceof DOMException ? error.name : '';
      if (!BENIGN_PLAY_ERRORS.has(name)) this.handlers.onMissing();
      return false;
    }
  }

  pause(): void {
    try {
      this.element.pause();
    } catch {
      /* l'élément peut être déjà détaché */
    }
  }

  /**
   * Positionne la lecture sur un extrait. Le temps est ramené dans la durée
   * réelle du morceau : les pads vont jusqu'à 47 s et rien ne garantit que le
   * fichier fourni soit aussi long.
   */
  async seekAndPlay(time: number): Promise<boolean> {
    if (this.destroyed) return false;
    this.ensureGraph();
    this.resume();
    this.seek(time);
    return this.play();
  }

  seek(time: number): void {
    if (!Number.isFinite(time) || time < 0) return;
    const { duration } = this;
    // Marge de 50 ms avant la fin : un seek exactement sur `duration` déclenche
    // `ended` au lieu de jouer l'extrait.
    const target = duration > 0 ? Math.min(time, Math.max(0, duration - 0.05)) : time;
    try {
      this.element.currentTime = target;
    } catch {
      /* seek impossible tant que les métadonnées ne sont pas chargées */
    }
  }

  /** Applique les quatre curseurs et l'ondulation. */
  applyMix(mix: MixState): void {
    if (this.destroyed) return;
    this.mix = mix;
    try {
      // Changer la vitesse doit changer la hauteur de la voix : c'est le gag.
      this.element.preservesPitch = false;
      this.element.mozPreservesPitch = false;
      this.element.webkitPreservesPitch = false;
      this.element.playbackRate = mix.rate;
    } catch {
      /* certains navigateurs refusent des vitesses extrêmes */
    }

    const context = this.context;
    if (!context || !this.filter || !this.wet || !this.outGain || !this.lfoGain) {
      // Sans graphe, le niveau reste réglable via l'élément lui-même.
      this.element.volume = Math.min(1, Math.max(0, mix.vol));
      return;
    }

    // Avec graphe, c'est `outGain` qui porte le niveau : l'élément repasse à
    // plein, sinon les deux atténuations se multiplieraient.
    this.element.volume = 1;

    // Rampes courtes plutôt qu'affectations sèches : évite les clics audibles
    // quand on tire un curseur.
    const now = context.currentTime;
    const ramp = (param: AudioParam, value: number): void => {
      try {
        param.setTargetAtTime(value, now, 0.01);
      } catch {
        param.value = value;
      }
    };

    ramp(this.filter.frequency, mix.cutoff);
    ramp(this.wet.gain, mix.echo);
    ramp(this.outGain.gain, mix.vol);
    ramp(this.lfoGain.gain, mix.wobble ? Math.min(1400, mix.cutoff * 0.6) : 0);
  }

  /**
   * Remplit `out` avec `out.length` niveaux entre 0 et 1, un par barre du
   * VU-mètre. Renvoie `false` si aucune donnée n'est disponible : l'appelant
   * laisse alors les barres à leur hauteur de repos.
   */
  readBars(out: Float32Array): boolean {
    const analyser = this.analyser;
    const bins = this.bins;
    if (!analyser || !bins || out.length === 0) return false;

    analyser.getByteFrequencyData(bins);
    const step = Math.max(1, Math.floor(bins.length / out.length));
    for (let bar = 0; bar < out.length; bar += 1) {
      let sum = 0;
      for (let offset = 0; offset < step; offset += 1) {
        sum += bins[bar * step + offset] ?? 0;
      }
      out[bar] = sum / step / 255;
    }
    return true;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.element.removeEventListener('error', this.reportMissing);
    this.pause();
    // Vider `src` interrompt le téléchargement en cours : sur un démontage
    // rapide, la requête ne reste pas en vol.
    this.element.removeAttribute('src');
    try {
      this.element.load();
    } catch {
      /* rien à faire de plus */
    }
    try {
      void this.context?.close();
    } catch {
      /* contexte déjà fermé */
    }
    this.context = null;
    this.analyser = null;
    this.bins = null;
  }
}
